// ── RingDisplay — unattended fullscreen TV surface ───────────────────────────
// Renders a public ring display for the spectators in the arena. No user
// interacts with it, and nobody can close a popup. Do NOT add Toasts, alerts,
// banners, or any error UI in this subtree. Errors belong in the telemetry
// (`logBackgroundError`, `trackException`), not on the screen. This route
// mounts OUTSIDE the app's `<ToastProvider>` on purpose, and `useToast()`
// throws when a component here calls it. If a component that you add needs a
// toast, it does not belong on the TV.
//
// #608 — the look: the layout of the broadcast overlay (#604) on a bright
// page (operator, 2026-10-15). A fixed 1920×1080 frame scales to the screen,
// so each TV shows the same layout. The data and the choice of the view come
// from the same feed as the overlay (`useRingFeed`, `components/ring`). The
// ring screens are in English only (operator, 2026-10-15).
// ─────────────────────────────────────────────────────────────────────────────
import { useParams } from 'react-router-dom';
import en from '../i18n/en';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRingFeed } from '../hooks/useRingFeed';
import { formatClock } from '../hooks/useRoundClock';
import { boutRounds } from '../utils/tournament';
import { boutCorner } from '../utils/boutCorner';
import FitText from '../components/ring/FitText';
import SilentBoundary from '../components/ring/SilentBoundary';
import {
  PANEL_COLOR, PANEL_SOFT, TIMER_COLOR, classLine, countryBorder, panelStateOf, ringViewOf, ringWords,
  roundFormulaOf, roundWinner, softText, stopRound, useFrameScale, type Corner, type PanelState as ResultState, type RingWords,
} from '../components/ring/ringView';
import type { Bout, BoutTimerPhase, BoutVerdict, PublicRoundScore } from '../types';

/** A corner panel: live, the winner or the loser of a result, or the card of the next bout. */
type PanelState = ResultState | 'next';

/** The winner panel of the TV also gets a light outline. */
const WINNER_RING: Record<Corner, string> = { red: 'ring-8 ring-red-200', blue: 'ring-8 ring-blue-200' };

const PILL_DARK = 'rounded-xl bg-slate-900 px-[26px] py-2.5 uppercase text-white';
const PILL_LIGHT = 'rounded-xl border-2 border-slate-300 bg-white px-6 py-2';
const CARD = 'rounded-3xl border-2 border-slate-300 bg-white';
/** The height of the NEXT strip. Its empty room takes the same height, so the
 *  panels do not move when the strip shows (operator, 2026-10-15). */
const STRIP_HEIGHT = 'h-[92px]';

/** The top bar: the ring, the bout, the stage, the class, the time formula. */
function TopBar({ bout, ringName, w }: { bout: Bout; ringName: string; w: RingWords }) {
  const line = classLine(bout, w);
  const formula = roundFormulaOf(bout);
  return (
    <div className="flex shrink-0 items-center gap-4 text-[34px] font-extrabold tracking-[0.02em]">
      {ringName && <span data-testid="ring-name" className={PILL_DARK}>{ringName}</span>}
      <span className="rounded-xl border-[3px] border-slate-900 bg-white px-[23px] py-[7px] uppercase">{w.T.common.bout} {bout.bout_number}</span>
      {bout.stage && <span className="rounded-xl bg-amber-500 px-[26px] py-2.5 uppercase text-slate-900">{w.stage(bout.stage)}</span>}
      {line && <span className={PILL_LIGHT}>{line}</span>}
      {formula && <span className={PILL_LIGHT}>{formula}</span>}
    </div>
  );
}

/** One corner: its label, the country, the last name that fits its panel, the first name, the team. */
function CornerPanel({ bout, corner, state, w, card }: { bout: Bout; corner: Corner; state: PanelState; w: RingWords; card?: boolean }) {
  const { last, first, team, country } = boutCorner(bout, corner);
  const loser = state === 'loser';
  const soft = softText(corner, state);
  const colour = `${PANEL_COLOR[corner][state === 'next' ? 'live' : state]} ${state === 'winner' ? WINNER_RING[corner] : ''}`;
  return (
    <div data-testid={`ring-${corner}`} data-state={state}
      className={`flex min-w-0 flex-col justify-center overflow-hidden rounded-[28px] ${card ? 'gap-3.5 px-12 py-11' : 'gap-[18px] p-12'} ${corner === 'blue' ? 'text-right' : ''} ${colour}`}>
      {/* A fixed height: the Winner label of a result is taller than the corner
          label (Red or Blue), and the names below must not move (operator, 2026-10-15). */}
      <div className={`flex h-16 items-center gap-5 ${corner === 'blue' ? 'flex-row-reverse' : ''}`}>
        {state === 'winner'
          ? <span className={`rounded-[10px] bg-white px-[18px] py-1.5 text-[34px] font-black uppercase tracking-[0.3em] ${PANEL_SOFT[corner].winner}`}>{w.T.common.winner}</span>
          : <span className={`text-[30px] font-extrabold uppercase tracking-[0.3em] ${loser ? PANEL_SOFT[corner].loser : soft}`}>{corner === 'red' ? w.T.common.red : w.T.common.blue}</span>}
        {country && (
          <span className={`rounded-[10px] border-[3px] px-3.5 py-0.5 text-[34px] font-extrabold tracking-[0.08em] ${countryBorder(corner, state)}`}>
            {country.toUpperCase()}
          </span>
        )}
      </div>
      {/* One height for each name: one line at 120 px (114 px) or two lines at
          64 px (122 px). A short name and a long name take the same room, so
          the rows of the two corners line up (operator, 2026-10-15). */}
      <div className="flex h-[122px] shrink-0 flex-col justify-end">
        <FitText text={last || '—'} max={card ? 110 : 120} min={64} floor={48} wrap className="font-black uppercase leading-[0.95]" testId={`ring-${corner}-last`} />
      </div>
      {/* #627 — line height 1.2, as the team line: 1.1 cut the tails of g, y
          and p and the ogoneks (ą, ę, į, ų). The clip box also gets the room
          of the last name (FitText, #608) for the caron of Ž, Č and Š. */}
      <span data-testid={`ring-${corner}-first`} className={`truncate py-[0.2em] -my-[0.2em] font-bold leading-[1.2] ${card ? 'text-[56px]' : 'text-[60px]'}`}>{first}</span>
      {/* Also with no team, the line keeps its height: the rows of the two
          corners line up (review of #608). */}
      <span data-testid={`ring-${corner}-team`} className={`min-h-[1.2em] truncate leading-[1.2] ${card ? 'text-[40px]' : 'text-[42px]'} ${soft}`}>{team}</span>
    </div>
  );
}

/** The middle of a live bout: the timer of the jury while the jury shows it,
 *  and one row for each round of the bout. A round shows a dash until the
 *  jury approves it. */
function LiveCenter({ bout, scores, timerLeft, timerPhase }: {
  bout: Bout; scores: PublicRoundScore[]; timerLeft: number | null; timerPhase: BoutTimerPhase;
}) {
  const byRound = new Map(scores.map(s => [s.round_number, s]));
  const rounds = Math.max(boutRounds(bout), ...scores.map(s => s.round_number));
  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/* #601 — the backup timer of the jury, while the jury shows it. No word: amber while stopped. */}
      {timerLeft !== null && (
        <div data-testid="ring-timer" data-phase={timerPhase} className={`${CARD} py-[18px] text-center`}>
          <span className={`font-mono text-[120px] font-extrabold leading-none tabular-nums ${TIMER_COLOR[timerPhase]}`}>{formatClock(timerLeft)}</span>
        </div>
      )}
      <div data-testid="ring-scores" className={`${CARD} flex min-h-0 flex-1 flex-col justify-center gap-[18px] px-5 py-7`}>
        {Array.from({ length: rounds }, (_, i) => i + 1).map(n => {
          const s = byRound.get(n);
          const won = s ? roundWinner(s) : null;
          return (
            <div key={n} data-testid={`ring-round-${n}`}
              className={`grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 text-[72px] font-black leading-none ${s ? '' : 'text-slate-300'}`}>
              <span className={`justify-self-end rounded-[14px] px-4 py-1 ${won === 'red' ? 'bg-red-600 text-white' : ''}`}>{s ? s.red_points : '–'}</span>
              <span className={`text-[32px] font-extrabold ${s ? 'text-slate-500' : 'text-slate-400'}`}>R{n}</span>
              <span className={`justify-self-start rounded-[14px] px-4 py-1 ${won === 'blue' ? 'bg-blue-600 text-white' : ''}`}>{s ? s.blue_points : '–'}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The middle of a result: the outcome, its name, and the round of a stop before the last round. */
function ResultCenter({ bout, verdict, w }: { bout: Bout; verdict: BoutVerdict; w: RingWords }) {
  const round = stopRound(bout, verdict);
  const name = w.outcome(verdict.outcome);
  return (
    <div className={`${CARD} flex min-w-0 flex-col items-center justify-center gap-[18px] px-5 py-8 text-center`}>
      {verdict.winner === 'draw'
        ? <FitText text={w.T.common.draw} max={112} min={56} className="font-black uppercase leading-none" testId="ring-draw" />
        : <>
            <FitText text={verdict.outcome ?? ''} max={132} min={64} className="font-black leading-none" testId="ring-outcome" />
            {name && <span className="text-[42px] font-bold leading-tight text-slate-700">{name}</span>}
          </>}
      {round && (
        <span className="mt-3 rounded-xl bg-slate-900 px-[22px] py-2 text-[36px] font-extrabold uppercase text-white">
          {w.T.bout.roundN.replace('{n}', String(round))}
        </span>
      )}
    </div>
  );
}

/** The bottom strip: the next bout of the ring. */
function NextStrip({ bout, w }: { bout: Bout; w: RingWords }) {
  return (
    <div data-testid="ring-next" className={`flex ${STRIP_HEIGHT} shrink-0 items-center gap-6 rounded-[18px] border-2 border-slate-300 bg-white px-8 text-[36px] font-bold`}>
      <span className="shrink-0 rounded-[10px] bg-amber-500 px-[18px] py-1 text-[30px] font-black uppercase tracking-[0.15em] text-slate-900">{w.T.overlay.next}</span>
      <span className="shrink-0 font-black uppercase">{w.T.common.bout} {bout.bout_number}</span>
      <span aria-hidden className="h-[18px] w-[18px] shrink-0 rounded-full bg-red-600" />
      <span className="min-w-0 truncate uppercase">{boutCorner(bout, 'red').last || '—'}</span>
      <span className="shrink-0 font-semibold text-slate-500">{w.T.common.vs}</span>
      <span aria-hidden className="h-[18px] w-[18px] shrink-0 rounded-full bg-blue-600" />
      <span className="min-w-0 truncate uppercase">{boutCorner(bout, 'blue').last || '—'}</span>
      <span className="ml-auto shrink-0 font-semibold text-slate-600">{classLine(bout, w)}</span>
    </div>
  );
}

/** Between bouts: the card of the next bout of the ring. */
function NextCard({ bout, w }: { bout: Bout; w: RingWords }) {
  const line = classLine(bout, w);
  const formula = roundFormulaOf(bout);
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-7">
      <div className="flex items-center gap-6">
        <span className="rounded-xl bg-amber-500 px-6 py-2 text-[40px] font-black uppercase tracking-[0.25em] text-slate-900">{w.T.overlay.next}</span>
        <span data-testid="ring-next-bout" className="text-[96px] font-black uppercase leading-none">{w.T.common.bout} {bout.bout_number}</span>
      </div>
      <div className="flex items-center gap-4 text-[34px] font-extrabold">
        {bout.stage && <span className="rounded-xl bg-amber-500 px-[26px] py-2.5 uppercase text-slate-900">{w.stage(bout.stage)}</span>}
        {line && <span className={PILL_LIGHT}>{line}</span>}
        {formula && <span className={PILL_LIGHT}>{formula}</span>}
      </div>
      <div className="mt-6 grid w-full grid-cols-[minmax(0,1fr)_160px_minmax(0,1fr)] items-stretch gap-6">
        <CornerPanel bout={bout} corner="red" state="next" w={w} card />
        <div className="flex items-center justify-center text-[72px] font-black uppercase text-slate-400">{w.T.common.vs}</div>
        <CornerPanel bout={bout} corner="blue" state="next" w={w} card />
      </div>
    </div>
  );
}

function Display() {
  const { tournamentId, ringId } = useParams<{ tournamentId: string; ringId: string }>();
  const w = ringWords(en);
  // #604 — the data of the ring comes from the hook that the broadcast overlay
  // uses too. The hook holds the reads, the events, the result (#454), the
  // unknown state (#457), the timer of the jury (#601) and the next bout (#608).
  const feed = useRingFeed(tournamentId, ringId, 'RingDisplay', { next: true });
  usePageTitle(feed.ringName && `${en.common.ring} ${feed.ringName}`);
  const scale = useFrameScale();

  const view = ringViewOf(feed);
  const winner = view.kind === 'result' ? view.verdict.winner : null;
  // The NEXT strip names the bout after the one on screen.
  const strip = (view.kind === 'live' || view.kind === 'result') && feed.nextBout && feed.nextBout.id !== view.bout.id
    ? feed.nextBout : null;

  return (
    <div className="flex h-screen w-full select-none items-center justify-center overflow-hidden bg-slate-100">
      <div data-testid="ring-display" data-view={view.kind} lang="en"
        className="flex h-[1080px] w-[1920px] shrink-0 flex-col gap-7 overflow-hidden px-16 pb-12 pt-14 text-slate-900"
        style={{ transform: `scale(${scale})` }}>
        {view.kind === 'live' || view.kind === 'result' ? (
          <>
            <TopBar bout={view.bout} ringName={feed.ringName} w={w} />
            <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_360px_minmax(0,1fr)] gap-6">
              <CornerPanel bout={view.bout} corner="red" state={panelStateOf(winner, 'red')} w={w} />
              {view.kind === 'live'
                ? <LiveCenter bout={view.bout} scores={view.scores} timerLeft={feed.timerLeft} timerPhase={feed.timerPhase} />
                : <ResultCenter bout={view.bout} verdict={view.verdict} w={w} />}
              <CornerPanel bout={view.bout} corner="blue" state={panelStateOf(winner, 'blue')} w={w} />
            </div>
            {/* The strip keeps its room while no bout waits: the panels keep their
                size, and the names do not move when it appears (operator, 2026-10-15). */}
            {strip ? <NextStrip bout={strip} w={w} /> : <div aria-hidden className={`${STRIP_HEIGHT} shrink-0`} />}
          </>
        ) : (
          <>
            {/* The height of the ring pill (71 px) also before the ring name appears:
                the text below must not move when the name shows (operator, 2026-10-15). */}
            <div className="flex min-h-[71px] shrink-0 items-center gap-4 text-[34px] font-extrabold tracking-[0.02em]">
              {feed.ringName && <span data-testid="ring-name" className={PILL_DARK}>{feed.ringName}</span>}
            </div>
            {view.kind === 'next'
              ? <NextCard bout={view.bout} w={w} />
              : (
                <div className="flex min-h-0 flex-1 items-center justify-center">
                  <span data-testid="ring-waiting" className="text-[64px] font-bold text-slate-500">{en.bout.waitingForNextBout}</span>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
}

export default function RingDisplay() {
  return <SilentBoundary context="RingDisplay"><Display /></SilentBoundary>;
}
